import { defer, finalize, shareReplay, Subscription } from 'rxjs';
import { ObservableSubscribe } from '../models/observable-subscribe';
import { TopicPayload } from '../models/topic-payload';

const replayableSubscriptions: { [topic: string]: ObservableSubscribe<TopicPayload<any>> } = {};

/**
 * Subscribes to an mqtt topic with replay capabilities.
 * Subsequent subscriptions to the same topic will receive the last messages.
 * When all subscribers unsubscribe, the replay buffer is cleared.
 * @param topic
 * @param bufferSize
 * @param getSubscription
 * @returns
 */
export function subscribeWithReplay<T>(topic: string, bufferSize: number | undefined, getSubscription: () => ObservableSubscribe<TopicPayload<T>>) {
    if (!replayableSubscriptions[topic]) {
        const subscription = getSubscription();
        const replayedSubscription = subscription.pipe(shareReplay({ bufferSize, refCount: true }));
        let sub: Subscription | undefined = replayedSubscription.subscribe(() => { });
        let count = 0;
        const observedSubscription = defer(() => {
            if (count === 0 && replayableSubscriptions[topic] !== assignedSubscription) {
                replayableSubscriptions[topic] = assignedSubscription;
            }
            count++;


            // Defer warm-sub teardown so refCount doesn't briefly hit 0 during handoff.
            if (sub) {
                const warmSub = sub;
                sub = undefined;
                setTimeout(() => warmSub.unsubscribe(), 0);
            }
            return replayedSubscription.pipe(finalize(() => {
                count--;
                if (count <= 0 && replayableSubscriptions[topic] === assignedSubscription) {
                    delete replayableSubscriptions[topic];
                }
            }));
        });
        const assignedSubscription = Object.assign(observedSubscription, { subscribed: subscription.subscribed });
        replayableSubscriptions[topic] = assignedSubscription;
    }
    return replayableSubscriptions[topic];
}
